/**
 * Account linking (settings → "Sign-in methods"): the platform's AccountLinking port, provided by the app shell.
 * Absent → no sign-in methods panel. Native: an `@Environment` value / `CompositionLocal`.
 */
import { SignInMethodsViewModel, type AccountLinking, type ApiClient, type LinkReturn, type SignInMethodsState } from "@bundle/core";
import { createContext, useCallback, useContext, useSyncExternalStore } from "react";
import { useOwned } from "../hooks/owned.ts";

export type AccountLinkingContextValue = {
  linking: AccountLinking;
  /** What the deployment offers (GET /v1/auth/methods). */
  api: Pick<ApiClient, "authMethods">;
  /** Where a web redirect comes back to: the page showing the panel (web: the current path). */
  returnTo: () => string;
  /** What the last redirect brought back (web: read and removed from the address bar, once). */
  takeReturn?: () => LinkReturn | undefined;
  /** The signed-in user (one view model per user). */
  userId: () => string;
};

export const AccountLinkingContext = createContext<AccountLinkingContextValue | null>(null);

/** The account linking services, or null where the app doesn't offer the panel. */
export function useAccountLinking(): AccountLinkingContextValue | null {
  return useContext(AccountLinkingContext);
}

/**
 * The sign-in methods view model for the signed-in user (one per user, shared by every mounted panel). Requires
 * AccountLinkingContext; use `SignInMethodsSlot` where it may be absent.
 */
export function useSignInMethods(): { vm: SignInMethodsViewModel; state: SignInMethodsState } {
  const ctx = useAccountLinking();
  if (ctx === null) throw new Error("AccountLinkingContext provider missing");
  const create = useCallback(() => new SignInMethodsViewModel({ linking: ctx.linking, api: ctx.api, returnTo: ctx.returnTo() }), [ctx]);
  const vm = useOwned(`signInMethods:${ctx.userId()}`, create);
  return { vm, state: useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot) };
}
